import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";

// CGPT Mars world layer.
// This file owns terrain presentation only. Gameplay coordinates, hex placement,
// resources and building state remain owned by the mature EM2c systems.

export function marsHeight(x, z) {
  const raw =
    (Math.sin(x * 0.18) + Math.cos(z * 0.15) + Math.sin((x + z) * 0.08)) * 0.34 +
    Math.sin(x * 0.62) * Math.cos(z * 0.53) * 0.08 -
    Math.exp(-(x * x + z * z) / 120) * 0.18;

  // Keep the colony core almost flat so fixed-size EM2c modules stay readable
  // while the surrounding Mars world gains real terrain depth.
  const radius = Math.hypot(x, z);
  const flatten = THREE.MathUtils.smoothstep(radius, 8, 22);
  return raw * flatten;
}

export function CGPTTerrain({ size = 170, segments = 170, ...meshProps }) {
  const geometry = useMemo(() => {
    const g = new THREE.PlaneGeometry(size, size, segments, segments);
    g.rotateX(-Math.PI / 2);

    const p = g.attributes.position;
    const colors = [];
    const low = new THREE.Color(0x5b1b09);
    const middle = new THREE.Color(0x8d3214);
    const high = new THREE.Color(0xa94a22);

    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i);
      const z = p.getZ(i);
      const y = marsHeight(x, z);
      p.setY(i, y);

      const t = THREE.MathUtils.clamp((y + 0.6) / 1.5, 0, 1);
      const c = t < 0.55
        ? low.clone().lerp(middle, t / 0.55)
        : middle.clone().lerp(high, (t - 0.55) / 0.45);
      colors.push(c.r, c.g, c.b);
    }

    g.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    p.needsUpdate = true;
    g.computeVertexNormals();
    return g;
  }, [size, segments]);

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <mesh {...meshProps} geometry={geometry} receiveShadow>
      <meshStandardMaterial vertexColors roughness={1} metalness={0.02} />
    </mesh>
  );
}

function seeded(seed) {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 4294967296;
  };
}

export function CGPTRocks({ count = 110, radius = 74 }) {
  const meshRef = useRef();
  const rocks = useMemo(() => {
    const rand = seeded(0x4d415253); // deterministic "MARS"
    const out = [];
    let guard = 0;
    while (out.length < count && guard++ < count * 20) {
      const x = (rand() - 0.5) * radius * 2;
      const z = (rand() - 0.5) * radius * 2;
      if (Math.hypot(x, z) < 10) continue;
      const s = 0.35 + rand() * 1.8;
      out.push({
        x,
        z,
        y: marsHeight(x, z) + 0.08,
        rx: rand() * 2,
        ry: rand() * 2,
        rz: rand() * 2,
        sx: s,
        sy: 0.55 * s,
        sz: 0.8 * s,
      });
    }
    return out;
  }, [count, radius]);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    rocks.forEach((r, i) => {
      dummy.position.set(r.x, r.y, r.z);
      dummy.rotation.set(r.rx, r.ry, r.rz);
      dummy.scale.set(r.sx, r.sy, r.sz);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    });
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [rocks]);

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, rocks.length]} castShadow receiveShadow>
      <dodecahedronGeometry args={[0.22, 0]} />
      <meshStandardMaterial color={0x351207} roughness={1} />
    </instancedMesh>
  );
}

export function CGPTAtmosphere({ stormActive = false }) {
  return (
    <>
      <color attach="background" args={[stormActive ? "#713019" : "#6b230d"]} />
      {/* No scene fog: terrain/buildings stay clear at all camera distances. */}
    </>
  );
}
